import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

interface IGeoData {
  raw?: {
    addresstype: 'building' | 'house' | 'town' | 'village' | 'city' | 'country';
    type: 'building' | 'house' | 'town' | 'village' | 'city' | 'country';
  };
  label: string;
  x?: number;
  y?: number;
}

interface UseLeafletMapOptions {
  geoData?: IGeoData[] | IGeoData | null;
  latitude?: number | null;
  longitude?: number | null;
  label?: string;
}

interface UseLeafletMapResult {
  mapReady: boolean;
  mapRef: React.MutableRefObject<L.Map | null>;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  geoDataArray: IGeoData[];
  hasData: boolean;
  error: string | null;
}

export const useLeafletMap = (options: UseLeafletMapOptions): UseLeafletMapResult => {
  const { geoData, latitude, longitude, label } = options;

  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [geoDataArray, setGeoDataArray] = useState<IGeoData[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Проста ініціалізація як у старих компонентах
  useEffect(() => {
    try {
      // Ініціалізуємо іконки Leaflet (як у старих компонентах)
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: markerIcon2x,
        iconUrl: markerIcon,
        shadowUrl: markerShadow,
      });

      console.log('Leaflet icons configured');
      setMapReady(true);
      setError(null);
    } catch (err) {
      console.error('Failed to initialize Leaflet:', err);
      setError('Failed to load map resources');
    }
  }, []);

  // Обробка geoData (як у старих компонентах)
  useEffect(() => {
    console.log('=== useLeafletMap: Processing geoData ===');
    console.log('geoData:', geoData);
    console.log('latitude:', latitude);
    console.log('longitude:', longitude);

    if (geoData) {
      const dataArray = Array.isArray(geoData) ? geoData : [geoData];
      console.log('Setting geoDataArray from geoData:', dataArray);
      setGeoDataArray(dataArray);
    } else if (latitude != null && longitude != null) {
      const singleGeoDataItem: IGeoData = {
        label: label || '',
        x: longitude,
        y: latitude,
      };
      console.log('Setting geoDataArray from lat/lng:', singleGeoDataItem);
      setGeoDataArray([singleGeoDataItem]);
    } else {
      console.log('No geo data available');
      setGeoDataArray([]);
    }
  }, [latitude, longitude, label, geoData]);

  // Fullscreen обробка
  useEffect(() => {
    const onFullScreenChange = () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
        if (document.fullscreenElement) {
          mapRef.current.scrollWheelZoom.enable();
          mapRef.current.touchZoom.enable();
          mapRef.current.dragging.enable();
        } else {
          mapRef.current.scrollWheelZoom.disable();
          mapRef.current.touchZoom.disable();
          mapRef.current.dragging.disable();
        }
      }
    };

    document.addEventListener('fullscreenchange', onFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullScreenChange);
    };
  }, []);

  // ResizeObserver - окремо від встановлення виду
  useEffect(() => {
    const map = mapRef.current;
    const container = containerRef.current;

    if (!map || !container) return;

    console.log('=== useLeafletMap: Setting up ResizeObserver ===');

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mapReady]);

  // Removed the useEffect that was setting the map view here.
  // This logic is now handled by the MapUpdater component inside UIMap.tsx.

  const hasData = geoDataArray.length > 0 && geoDataArray.some(d => d.x != null && d.y != null);

  return {
    mapReady,
    mapRef,
    containerRef,
    geoDataArray,
    hasData,
    error,
  };
};